import { IPacingAdvertiser } from '@/Interfaces/Advertiser.interface';
import { IBeeswaxCampaign } from '@/Interfaces/Beeswax/Campaign.interface';
import {
  IClientControlOption,
  IControlOption,
  IGroupedControlOption,
  IWhitelabelControlOption,
} from '@/Interfaces/ControlOption.interface';
import { IPacingFilters } from '@/Interfaces/Pacing/Filters.interface';
import { IPacingReportRecord } from '@/Interfaces/Pacing/ReportRecord.interface';
import { IBaseDataReport } from '@/Interfaces/ReportingDashboard/Beeswax/Report.interface';
import { IAbortSignalThunkArg } from '@/Interfaces/Slices/AsyncThunk.interface';
import { MAX_BEESWAX_0_5_REQUEST_ROWS, MAX_QUERY_PARAMS_LENGTH } from '@/constants/axios';
import { BEESWAX_CAMPAIGNS_ENDPOINT } from '@/constants/endpoints';
import { getHighestLevelAdvertisers } from '@/helpers/advertisers';
import batchRequest from '@/helpers/batchRequest';
import { getDefaultFilters } from '@/helpers/pacing';
import { beeswaxPacingReportHandler, mapBeeswaxCampaigns } from '@/helpers/pacing/beeswax';
import { getGoogleCampaignFilterOptions, googlePacingReportHandler } from '@/helpers/pacing/google';
import { fulfilledCaseHandler, pendingCaseHandler, rejectedCaseHandler } from '@/store/asyncThunkHandlers';
import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import axios from 'axios';

enum ActionPrefix {
  GetBeeswaxPacingReport = 'pacingSlice/getBeeswaxPacingReport',
  GetGooglePacingReport = 'pacingSlice/getGooglePacingReport',
  GetClients = 'pacingSlice/getClients',
  GetCampaigns = 'pacingSlice/getCampaigns',
  GetWhitelabels = 'pacingSlice/getWhitelabels',
}

interface IState {
  filters: IPacingFilters;
  beeswaxPacingData: IPacingReportRecord[];
  beeswaxPacingDataRequestId: boolean | string | null;
  noBeeswaxPacingData: boolean;
  googlePacingData: IPacingReportRecord[];
  googlePacingDataRequestId: boolean | string | null;
  noGooglePacingData: boolean;
  campaigns: IGroupedControlOption[];
  campaignsRequestId: boolean | string | null;
}

const initialState: IState = {
  filters: getDefaultFilters(),
  beeswaxPacingData: [],
  beeswaxPacingDataRequestId: true,
  noBeeswaxPacingData: false,
  googlePacingData: [],
  googlePacingDataRequestId: true,
  noGooglePacingData: false,
  campaigns: [],
  campaignsRequestId: true,
};

export const pacingSlice = createSlice({
  name: 'pacing',
  initialState,
  reducers: {
    setFilters: (state, action) => {
      state.filters = {
        ...state.filters,
        ...action.payload,
      };
    },
    resetFilters: state => {
      state.filters = getDefaultFilters();
    },
    resetPacingPageState: () => initialState,
  },
  extraReducers: builder => {
    builder
      .addCase(getBeeswaxPacingReport.pending, (state, action) => {
        state.beeswaxPacingDataRequestId = action.meta.requestId;
        state.noBeeswaxPacingData = false;
        state.beeswaxPacingData = [];
      })
      .addCase(getBeeswaxPacingReport.rejected, rejectedCaseHandler('beeswaxPacingData', []))
      .addCase(getBeeswaxPacingReport.fulfilled, (state, action) => {
        if (state.beeswaxPacingDataRequestId === action.meta.requestId) {
          state.noBeeswaxPacingData = !action.payload.report?.length;
          state.beeswaxPacingData = action.payload.report;
          state.beeswaxPacingDataRequestId = null;
        }
      })
      .addCase(getGooglePacingReport.pending, (state, action) => {
        state.googlePacingDataRequestId = action.meta.requestId;
        state.noGooglePacingData = false;
        state.googlePacingData = [];
      })
      .addCase(getGooglePacingReport.rejected, rejectedCaseHandler('googlePacingData', []))
      .addCase(getGooglePacingReport.fulfilled, (state, action) => {
        if (state.googlePacingDataRequestId === action.meta.requestId) {
          state.noGooglePacingData = !action.payload?.length;
          state.googlePacingData = action.payload;
          state.googlePacingDataRequestId = null;
        }
      })
      .addCase(getCampaigns.pending, pendingCaseHandler('campaigns'))
      .addCase(getCampaigns.rejected, rejectedCaseHandler('campaigns'))
      .addCase(getCampaigns.fulfilled, fulfilledCaseHandler('campaigns'));
  },
});

export const { setFilters, resetFilters, resetPacingPageState } = pacingSlice.actions;

export const getBeeswaxPacingReport = createAsyncThunk<IBaseDataReport<IPacingReportRecord>, IAbortSignalThunkArg>(
  ActionPrefix.GetBeeswaxPacingReport,
  beeswaxPacingReportHandler,
);

export const getGooglePacingReport = createAsyncThunk<IPacingReportRecord[], IAbortSignalThunkArg>(
  ActionPrefix.GetGooglePacingReport,
  googlePacingReportHandler,
);

interface IGetCampaignsPayload extends IAbortSignalThunkArg {
  advertisers: IPacingAdvertiser[];
  clients: IClientControlOption<IPacingAdvertiser>[];
  whitelabels: IWhitelabelControlOption<IPacingAdvertiser>[];
}

// TODO: use reporting data
export const getCampaigns = createAsyncThunk<IControlOption[], IGetCampaignsPayload>(
  ActionPrefix.GetCampaigns,
  async payload => {
    const { advertisers, clients, whitelabels } = payload;
    const beeswaxAdvertiserIds = getHighestLevelAdvertisers(whitelabels, clients, advertisers, 'beeswaxId').filter(
      (id): id is number => id !== undefined,
    );
    const ribeyeAdvertiserIds = getHighestLevelAdvertisers(whitelabels, clients, advertisers, 'value');

    if (!beeswaxAdvertiserIds?.length) {
      return [];
    }

    const batchFetchCampaigns = batchRequest<number, IBeeswaxCampaign[]>(async advertiserId => {
      const response = await axios.get(BEESWAX_CAMPAIGNS_ENDPOINT, {
        params: {
          rows: MAX_BEESWAX_0_5_REQUEST_ROWS,
          advertiser_id: advertiserId,
          fields: ['advertiser_id', 'campaign_name', 'campaign_id'],
          sort_by: ['campaign_name'],
        },
        signal: payload?.signal,
      });
      return response.data?.payload || [];
    }, MAX_QUERY_PARAMS_LENGTH);

    const [beeswaxCampaigns, googleCampaigns] = await Promise.all([
      batchFetchCampaigns(beeswaxAdvertiserIds),
      getGoogleCampaignFilterOptions(ribeyeAdvertiserIds),
    ]);

    return [...mapBeeswaxCampaigns(beeswaxCampaigns), ...googleCampaigns];
  },
);

export default pacingSlice.reducer;
